import firebase from '@/firebase'
import errorLogger, { ERROR_LOG_FUNCTION_NAME } from './errorLogger'

// The most functionData we will carry into a report. sanitizeData copies the failed call's
// payload verbatim and the server writes the whole report into a single Firestore document, so
// an oversized payload makes the report itself unwritable - and a report that cannot be written
// is one that can never stop failing.
const MAX_FUNCTION_DATA_LENGTH = 20000

class FirebaseFunctionsInterceptor {
  constructor () {
    this.originalHttpsCallable = null
  }

  init () {
    if (this.originalHttpsCallable) {
      return
    }

    this.originalHttpsCallable = firebase.functions.httpsCallable.bind(firebase.functions)

    firebase.functions.httpsCallable = (name, options) => {
      const originalCallable = this.originalHttpsCallable(name, options)

      return (data) => {
        const startTime = Date.now()

        return originalCallable(data).catch((error) => {
          // Never report the reporting call itself. errorLogger sends its payload through this
          // very wrapper, so logging its failure calls it again, and again, with no exit: each
          // request carries the previous one inside functionData, so the body grows on every
          // turn, and once it passes Firestore's 1 MiB document limit the server can no longer
          // write it, which makes the failure - and therefore the loop - permanent. The rate
          // limiter now brakes this from the other side as well.
          if (name === ERROR_LOG_FUNCTION_NAME) {
            console.error(`Failed to log error through ${name}:`, error)
            throw error
          }

          const duration = Date.now() - startTime

          errorLogger.logError(error, {
            functionName: name,
            functionData: this.sanitizeData(data),
            duration: duration,
            errorCode: error.code || 'unknown',
            errorDetails: error.details || null,
            httpErrorCode: this.extractHttpErrorCode(error),
            timestamp: new Date().toISOString()
          }, 'firebase-function')

          throw error
        })
      }
    }
  }

  sanitizeData (data) {
    if (!data || typeof data !== 'object') {
      return data
    }

    const sanitized = { ...data }

    const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'auth', 'authorization']
    sensitiveKeys.forEach(key => {
      if (sanitized[key]) {
        sanitized[key] = '[REDACTED]'
      }
    })

    // Keep the report small enough to be stored: a payload that does not fit is replaced by a
    // note of its size, so a large call's failure is still reported instead of being lost.
    let serialized = null
    try {
      serialized = JSON.stringify(sanitized)
    } catch (error) {
      return { omitted: 'functionData could not be serialized' }
    }
    if (serialized && serialized.length > MAX_FUNCTION_DATA_LENGTH) {
      return { omitted: `functionData omitted (${serialized.length} characters)` }
    }

    return sanitized
  }

  extractHttpErrorCode (error) {
    if (error.message && error.message.includes('500')) {
      return 500
    }
    if (error.message && error.message.includes('404')) {
      return 404
    }
    if (error.message && error.message.includes('403')) {
      return 403
    }
    if (error.message && error.message.includes('401')) {
      return 401
    }
    if (error.code === 'functions/internal') {
      return 500
    }
    if (error.code === 'functions/not-found') {
      return 404
    }
    if (error.code === 'functions/permission-denied') {
      return 403
    }
    if (error.code === 'functions/unauthenticated') {
      return 401
    }
    return null
  }
}

export default new FirebaseFunctionsInterceptor()
